import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { format } from 'date-fns';
import { formatIST, fmtTime, fmtTimeSec, fmtDateShort, fmtDateTime, fmtMd } from '../utils/time';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { MAP_TILES, DEFAULT_TILE } from '../utils/mapTiles';
import ReportIncidentModal from '../components/ReportIncidentModal';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const API_BASE = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname.includes('pages.dev') ? 'https://guard-patrol-73go.onrender.com' : '');
const SEV = { low: '#22c55e', medium: '#f59e0b', high: '#ef233c', critical: '#7c3aed' };

const isValidCoord = (lat, lng) => {
  if (lat == null || lng == null) return false;
  const nLat = parseFloat(lat);
  const nLng = parseFloat(lng);
  if (isNaN(nLat) || isNaN(nLng)) return false;
  if (nLat === 0 && nLng === 0) return false;
  return true;
};


// Build full image URL — supports base64 directly from DB and full URLs across Cloudflare + Render
const imgUrl = (cpOrPath) => {
  if (!cpOrPath) return null;
  if (typeof cpOrPath === 'object') {
    if (cpOrPath.image_base64) return `data:image/jpeg;base64,${cpOrPath.image_base64}`;
    if (cpOrPath.image_url) return imgUrl(cpOrPath.image_url);
    return null;
  }
  const s = String(cpOrPath);
  if (s.startsWith('data:') || s.startsWith('http')) return s;
  return `${API_BASE}${s}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// SCAN MODAL — shown when guard taps a checkpoint
// ─────────────────────────────────────────────────────────────────────────────
function ScanModal({ cp, patrolId, onClose, onScanned }) {
  const isGps = cp.scan_type === 'gps';

  const [photo, setPhoto]       = useState(null);
  const [preview, setPreview]   = useState(null);
  const [notes, setNotes]       = useState('');
  const [submitting, setSub]    = useState(false);
  const [gettingGps, setGps]    = useState(false);
  const [coords, setCoords]     = useState(null);
  const cameraRef = useRef();

  // Auto-get GPS on open
  useEffect(() => {
    setGps(true);
    navigator.geolocation?.getCurrentPosition(
      pos => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy });
        setGps(false);
      },
      () => { setGps(false); toast.error('Could not get GPS — scan may fail'); }
    );
  }, []);

  // ── Stamp timestamp + GPS onto captured image with smart client-side downscaling ────────────────
  const stampImage = useCallback((file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        // Downscale to max 1280px for fast mobile upload and high clarity (reduces 5MB to ~150KB)
        const MAX_DIM = 1280;
        let width = img.width;
        let height = img.height;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width  = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const now = new Date();
        const stamp   = format(now, 'dd/MM/yyyy HH:mm:ss');
        const gpsText = coords ? `GPS: ${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` : 'GPS: unavailable';
        const cpText  = `CP${cp.sequence_order}: ${cp.name}`;
        const lines   = [stamp, gpsText, cpText];
        const fontSize = Math.max(16, Math.round(width * 0.03));

        const bannerH = fontSize * lines.length * 1.7 + 12;
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.fillRect(0, height - bannerH, width, bannerH);
        ctx.fillStyle = 'white';
        ctx.font = `bold ${fontSize}px monospace`;
        ctx.textBaseline = 'bottom';
        lines.forEach((line, i) => {
          ctx.fillText(line, 12, height - (lines.length - 1 - i) * fontSize * 1.7 - 8);
        });

        URL.revokeObjectURL(url);
        canvas.toBlob(blob => {
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.80);
      };
      img.src = url;
    });
  }, [cp, coords]);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const stamped = await stampImage(file);
    setPhoto(stamped);
    setPreview(URL.createObjectURL(stamped));
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!isGps && !photo)  return toast.error('Please take a photo first');
    if (isGps && !coords)  return toast.error('Waiting for GPS location…');
    setSub(true);
    try {
      const fd = new FormData();
      fd.append('patrol_id',       patrolId);
      fd.append('checkpoint_id',   cp.id);
      fd.append('latitude',        coords ? coords.lat.toFixed(7) : '');
      fd.append('longitude',       coords ? coords.lng.toFixed(7) : '');
      fd.append('accuracy_meters', coords?.acc ? coords.acc.toFixed(1) : '10');
      fd.append('notes',           notes);
      if (!isGps && photo) fd.append('image', photo);

      const res = await axios.post('/api/scans', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const status = res.data?.verification?.status;
      if (status === 'verified') {
        toast.success(`✅ CP${cp.sequence_order} "${cp.name}" verified!`);
        onScanned();
        onClose();
      } else {
        toast.error(res.data?.verification?.message || 'Scan failed — out of range');
        setSub(false);
      }
    } catch (err) {
      // Show the detailed out-of-range message if available
      const msg = err.response?.data?.verification?.message
               || err.response?.data?.error
               || 'Scan failed';
      toast.error(msg, { duration: 5000 });
      setSub(false);
    }
  };

  const canSubmit = isGps ? !!coords : !!photo;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,20,50,0.55)', backdropFilter: 'blur(4px)', zIndex: 1100,
      }} />

      {/* Bottom sheet modal */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'white', borderRadius: '20px 20px 0 0',
        zIndex: 1101, padding: '20px 20px 32px',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Handle */}
        <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 2, margin: '0 auto 18px' }} />

        {/* CP info + type badge */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 12, flexShrink: 0,
            background: isGps
              ? 'linear-gradient(135deg,#22c55e,#16a34a)'
              : 'linear-gradient(135deg,#4361ee,#7b8ff8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
          }}>{isGps ? '📡' : '📷'}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>
              CP{cp.sequence_order}: {cp.name}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 3, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 700,
                background: isGps ? '#dcfce7' : '#eef2ff',
                color: isGps ? '#16a34a' : '#4361ee',
              }}>
                {isGps ? '📡 GPS Only' : '📷 Photo + GPS'}
              </span>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {gettingGps
                  ? '⏳ Getting GPS…'
                  : coords
                  ? `📡 Ready · ±${coords.acc?.toFixed(0)}m`
                  : '⚠️ GPS unavailable'}
              </span>
            </div>
          </div>
        </div>

        {/* ── GPS-ONLY MODE ─────────────────────────────────── */}
        {isGps && (
          <div style={{
            background: '#f0fdf4', border: '2px solid #86efac',
            borderRadius: 14, padding: '20px 16px', marginBottom: 16, textAlign: 'center',
          }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📡</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#15803d', marginBottom: 6 }}>
              GPS Verification
            </div>
            <div style={{ fontSize: 13, color: '#166534', marginBottom: 12 }}>
              {coords
                ? `Your location is detected.\nTap Verify to confirm you're at this checkpoint.`
                : 'Waiting for GPS signal…'}
            </div>
            {coords && (
              <div style={{
                background: 'white', borderRadius: 8, padding: '8px 12px',
                fontSize: 11, color: '#64748b', fontFamily: 'monospace',
                display: 'inline-block',
              }}>
                {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}&nbsp;&nbsp;±{coords.acc?.toFixed(0)}m
              </div>
            )}
          </div>
        )}

        {/* ── PHOTO MODE ────────────────────────────────────── */}
        {!isGps && (
          <>
            {preview ? (
              <div style={{ marginBottom: 14, position: 'relative' }}>
                <img src={preview} alt="preview" style={{
                  width: '100%', maxHeight: 280, objectFit: 'cover',
                  borderRadius: 12, border: '2px solid #22c55e', display: 'block',
                }} />
                <button onClick={() => { setPhoto(null); setPreview(null); cameraRef.current?.click(); }}
                  style={{
                    position: 'absolute', bottom: 10, right: 10,
                    background: 'rgba(0,0,0,0.65)', border: 'none', color: 'white',
                    padding: '7px 14px', borderRadius: 20, cursor: 'pointer',
                    fontSize: 13, fontWeight: 600,
                  }}>📷 Retake</button>
              </div>
            ) : (
              <button onClick={() => cameraRef.current?.click()}
                style={{
                  width: '100%', padding: '22px 10px', borderRadius: 14,
                  border: '2px solid #4361ee', background: '#eef2ff',
                  color: '#4361ee', cursor: 'pointer', fontWeight: 700, fontSize: 16,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  marginBottom: 14, WebkitTapHighlightColor: 'transparent',
                }}>
                <span style={{ fontSize: 40 }}>📷</span>
                Take Photo
                <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>
                  Timestamp will be stamped automatically
                </span>
              </button>
            )}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }} onChange={handleCapture} />
          </>
        )}

        {/* Notes */}
        <textarea className="form-input" rows={2}
          placeholder="Notes (optional) — e.g. all clear, door locked…"
          value={notes} onChange={e => setNotes(e.target.value)}
          style={{ resize: 'none', marginBottom: 16, fontSize: 13 }} />

        {/* Submit */}
        <button onClick={handleSubmit} disabled={submitting || !canSubmit}
          style={{
            width: '100%', padding: '14px', borderRadius: 12, border: 'none',
            background: (!canSubmit || submitting)
              ? '#f1f5f9'
              : isGps
              ? 'linear-gradient(135deg,#22c55e,#16a34a)'
              : 'linear-gradient(135deg,#22c55e,#16a34a)',
            color: (!canSubmit || submitting) ? '#94a3b8' : 'white',
            fontWeight: 800, fontSize: 16,
            cursor: !canSubmit ? 'not-allowed' : 'pointer',
            boxShadow: !canSubmit ? 'none' : '0 4px 16px rgba(34,197,94,0.4)',
            transition: 'all .2s', WebkitTapHighlightColor: 'transparent',
          }}>
          {submitting
            ? '⏳ Verifying…'
            : isGps
            ? '📡 Verify Location'
            : '✅ Submit Scan'}
        </button>

        <button onClick={onClose}
          style={{
            width: '100%', marginTop: 10, padding: '12px', borderRadius: 12,
            border: 'none', background: '#f1f5f9', color: '#64748b',
            fontWeight: 600, fontSize: 14, cursor: 'pointer',
          }}>
          Cancel
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PATROL DETAIL PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function PatrolDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();

  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [scanningCp, setScanningCp] = useState(null); // checkpoint being scanned
  const [ending,     setEnding]     = useState(false);
  const [tile,       setTile]       = useState(DEFAULT_TILE);
  const [previewModalCp, setPreviewModalCp] = useState(null);
  const [showIncidentModal, setShowIncidentModal] = useState(false);

  const isGuard = user?.role === 'guard';
  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  const load = useCallback(() => {
    setError(null);
    axios.get(`/api/reports/patrol/${id}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch((err) => {
        setLoading(false);
        setError(err.response?.data?.error || 'Unable to load patrol data');
      });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 10s while patrol is active
  useEffect(() => {
    if (!data) return;
    if (data.patrol?.status !== 'active') return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [data, load]);

  const patrol = data?.patrol;
  const isActive = patrol?.status === 'active';
  const isMyPatrol = isGuard
    ? String(patrol?.guard_id || '').toLowerCase() === String(user?.id || '').toLowerCase()
    : true;
  const showScanInterface = isActive && isMyPatrol;

  // ── Periodic GPS Ping for Live Guard Tracking (only while active guard is patrolling) ──
  useEffect(() => {
    if (!isActive || !showScanInterface) return;

    const sendPing = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          axios.post(`/api/patrols/${id}/location`, {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy_meters: pos.coords.accuracy || 10,
          }).catch(() => {});
        },
        (err) => console.warn('Live location ping error:', err),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 }
      );
    };

    sendPing();
    const timer = setInterval(sendPing, 12000);
    return () => clearInterval(timer);
  }, [isActive, showScanInterface, id]);

  const handleEndPatrol = async () => {
    if (!window.confirm('End this patrol? This cannot be undone.')) return;
    setEnding(true);
    try {
      await axios.post(`/api/patrols/${id}/end`);
      toast.success('Patrol completed!');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to end patrol');
    } finally {
      setEnding(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>🚶</div>
      <div style={{ color: '#64748b', fontSize: 15 }}>Loading patrol…</div>
    </div>
  );

  if (error) return (
    <div style={{ maxWidth: 480, margin: '60px auto', textAlign: 'center', padding: '32px 24px', background: 'white', borderRadius: 16, border: '1px solid #fee2e2', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontWeight: 800, fontSize: 17, color: '#991b1b', marginBottom: 8 }}>Unable to Load Patrol</div>
      <div style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>{error}</div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button onClick={() => navigate('/patrols')} style={{ padding: '9px 18px', borderRadius: 10, border: '1px solid #cbd5e1', background: 'white', color: '#475569', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          ← Back to Patrols
        </button>
        <button onClick={load} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: '#4361ee', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          🔄 Retry
        </button>
      </div>
    </div>
  );

  if (!data || !patrol) return (
    <div style={{ maxWidth: 480, margin: '60px auto', textAlign: 'center', padding: '32px 24px', background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
      <div style={{ fontWeight: 800, fontSize: 17, color: '#1e293b', marginBottom: 8 }}>Patrol Not Found</div>
      <div style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>This patrol may have been removed or does not exist.</div>
      <button onClick={() => navigate('/patrols')} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: '#4361ee', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
        ← Back to Patrols
      </button>
    </div>
  );

  const { checkpoints = [], incidents = [] } = data;

  const scannedIds = checkpoints
    .filter(cp => cp.scanned_at && cp.status === 'verified')
    .map(cp => cp.id);

  const compliance = Math.min(100, Math.max(0, parseFloat(patrol.compliance_percent) || 0));
  // Only use checkpoints with valid GPS for map center
  const gpsCheckpoints = checkpoints.filter(cp => isValidCoord(cp.latitude, cp.longitude));
  const mapCenter = gpsCheckpoints.length > 0
    ? [parseFloat(gpsCheckpoints[0].latitude), parseFloat(gpsCheckpoints[0].longitude)]
    : [22.2587, 71.1924];

  // ──────────────────────────────────────────────────────────
  // ACTIVE PATROL — GUARD SCAN INTERFACE
  // ──────────────────────────────────────────────────────────
  if (showScanInterface) {
    return (
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,#1a1a2e,#16213e)',
          borderRadius: 16, padding: '18px 20px', marginBottom: 16,
          color: 'white',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 17 }}>🚶 Active Patrol</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 3 }}>{patrol.route_name}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>🏢 {patrol.site_name}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: compliance >= 80 ? '#22c55e' : '#f59e0b' }}>
                {compliance}%
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>compliance</div>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>
              <span>{scannedIds.length} of {checkpoints.length} scanned</span>
              <span>Started {fmtTime(patrol.started_at)}</span>
            </div>
            <div style={{ height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 4, transition: 'width .5s',
                background: compliance >= 80 ? '#22c55e' : '#f59e0b',
                width: `${checkpoints.length > 0 ? (scannedIds.length / checkpoints.length) * 100 : 0}%`,
              }} />
            </div>
          </div>
        </div>

        {/* Checkpoints list */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 10 }}>
            📍 Checkpoints
          </div>

          {checkpoints.length === 0 && (
            <div className="card" style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>
              No checkpoints defined for this route
            </div>
          )}

          {checkpoints.map((cp, i) => {
            const done   = scannedIds.includes(cp.id);
            const next   = !done && checkpoints.findIndex(c => !scannedIds.includes(c.id)) === i;

            return (
              <div key={cp.id} style={{
                background: 'white', borderRadius: 14, padding: '14px 16px',
                marginBottom: 10,
                border: `2px solid ${done ? '#22c55e' : next ? '#4361ee' : '#e8ecf4'}`,
                boxShadow: next ? '0 4px 16px rgba(67,97,238,0.15)' : '0 2px 6px rgba(0,0,0,.04)',
                transition: 'all .2s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Status icon */}
                  <div style={{
                    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                    background: done ? '#dcfce7' : next ? '#eef2ff' : '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20,
                  }}>
                    {done ? '✅' : next ? '📍' : '⭕'}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
                      CP{cp.sequence_order}: {cp.name}
                    </div>
                    {done && cp.scanned_at && (
                      <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>
                        ✓ Scanned at {fmtTimeSec(cp.scanned_at)}
                        {cp.distance_from_checkpoint != null && ` · ${cp.distance_from_checkpoint}m away`}
                      </div>
                    )}
                    {!done && next && (
                      <div style={{ fontSize: 11, color: '#4361ee', marginTop: 2, fontWeight: 600 }}>
                        ← {cp.scan_type === 'gps' ? '📡 GPS verify next' : '📷 Scan this next'}
                      </div>
                    )}
                    {!done && !next && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Pending</div>
                    )}
                  </div>

                  {/* Thumbnail if scanned */}
                  {done && (cp.image_url || cp.image_base64) && (
                    <img src={imgUrl(cp)} alt="scan"
                      style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                  )}

                  {/* Scan button */}
                  {!done && (
                    <button
                      onClick={() => setScanningCp(cp)}
                      style={{
                        padding: '9px 14px', borderRadius: 10, border: 'none',
                        background: next
                          ? cp.scan_type === 'gps'
                            ? 'linear-gradient(135deg,#22c55e,#16a34a)'
                            : 'linear-gradient(135deg,#4361ee,#7b8ff8)'
                          : '#f1f5f9',
                        color: next ? 'white' : '#64748b',
                        fontWeight: 700, fontSize: 13, cursor: 'pointer',
                        boxShadow: next ? '0 3px 10px rgba(67,97,238,0.3)' : 'none',
                        flexShrink: 0,
                        WebkitTapHighlightColor: 'transparent',
                      }}>
                      {cp.scan_type === 'gps' ? '📡 Verify' : '📷 Scan'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons: Report Incident + End Patrol */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
          <button
            type="button"
            onClick={() => setShowIncidentModal(true)}
            style={{
              padding: '14px', borderRadius: 12, border: '1.5px solid #fecdd3',
              background: '#fff1f2', color: '#e11d48',
              fontWeight: 800, fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              boxShadow: '0 2px 8px rgba(225,29,72,0.12)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            ⚠️ Report Incident
          </button>
          <button
            type="button"
            onClick={handleEndPatrol}
            disabled={ending}
            style={{
              padding: '14px', borderRadius: 12, border: 'none',
              background: ending ? '#f1f5f9' : 'linear-gradient(135deg,#ef233c,#f87171)',
              color: ending ? '#94a3b8' : 'white',
              fontWeight: 800, fontSize: 14, cursor: ending ? 'not-allowed' : 'pointer',
              boxShadow: ending ? 'none' : '0 4px 16px rgba(239,35,60,0.3)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {ending ? '⏳ Ending…' : '🏁 End Patrol'}
          </button>
        </div>

        {/* Scan Modal */}
        {scanningCp && (
          <ScanModal
            cp={scanningCp}
            patrolId={id}
            onClose={() => setScanningCp(null)}
            onScanned={load}
          />
        )}

        {/* Incident Report Modal during Active Patrol */}
        {showIncidentModal && (
          <ReportIncidentModal
            patrolId={id}
            onClose={() => setShowIncidentModal(false)}
            onSuccess={load}
          />
        )}
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────
  // COMPLETED / REPORT VIEW
  // ──────────────────────────────────────────────────────────
  const statusStyle = {
    active:    { bg: '#fef3c7', color: '#92400e' },
    completed: { bg: '#dcfce7', color: '#166534' },
    abandoned: { bg: '#fee2e2', color: '#991b1b' },
  };
  const ss = statusStyle[patrol.status] || { bg: '#f3f4f6', color: '#374151' };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">📋 Patrol Report</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
            👮 {patrol.guard_name} · {patrol.route_name}
          </div>
        </div>
        <span style={{
          fontSize: 12, padding: '4px 14px', borderRadius: 20, fontWeight: 700,
          background: ss.bg, color: ss.color, textTransform: 'capitalize',
        }}>{patrol.status}</span>
      </div>

      {/* Summary cards */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))',
        gap: 12, marginBottom: 20,
      }}>
        {[
          { label: 'Route',       value: patrol.route_name,   icon: '📍' },
          { label: 'Site',        value: patrol.site_name,    icon: '🏢' },
          { label: 'Compliance',  value: `${compliance}%`,   icon: '📊',
            color: compliance >= 80 ? '#16a34a' : compliance >= 60 ? '#d97706' : '#dc2626' },
          { label: 'Duration',    value: patrol.duration_minutes ? `${patrol.duration_minutes}m` : '—', icon: '⏱️' },
          { label: 'Checkpoints', value: `${patrol.completed_checkpoints}/${patrol.total_checkpoints}`, icon: '✅' },
          { label: 'Started',     value: fmtDateShort(patrol.started_at), icon: '🕐' },
        ].map((c, i) => (
          <div key={i} className="card" style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 6 }}>
              {c.icon} {c.label}
            </div>
            <div style={{ fontWeight: 700, fontSize: 15, color: c.color || '#1e293b' }}>
              {c.value}
            </div>
          </div>
        ))}
      </div>

      <div className="dash-charts-grid" style={{ marginBottom: 20 }}>
        {/* Map */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', minHeight: 320 }}>
          <div style={{ display: 'flex', gap: 6, padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            {Object.values(MAP_TILES).map(t => (
              <button key={t.id} onClick={() => setTile(t.id)}
                style={{
                  padding: '3px 8px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11,
                  background: tile === t.id ? '#4361ee' : '#e2e8f0',
                  color: tile === t.id ? 'white' : '#334155', fontWeight: tile === t.id ? 600 : 400,
                }}>
                {t.name}
              </button>
            ))}
          </div>
          <MapContainer center={mapCenter} zoom={17} maxZoom={22} style={{ height: 340, width: '100%' }}>
            <TileLayer
              key={tile}
              url={MAP_TILES[tile]?.url || MAP_TILES.googleHybrid.url}
              attribution={MAP_TILES[tile]?.attr || '© Google Maps'}
              maxZoom={22}
              maxNativeZoom={MAP_TILES[tile]?.maxNativeZoom || 21}
            />
            {checkpoints.filter(cp => isValidCoord(cp.latitude, cp.longitude)).map(cp => (
              <React.Fragment key={cp.id}>
                <Circle center={[parseFloat(cp.latitude), parseFloat(cp.longitude)]} radius={cp.radius_meters || 30}
                  color={cp.scanned_at ? '#22c55e' : (isActive ? '#f59e0b' : '#ef233c')} fillOpacity={0.2} weight={2} />
                <Marker position={[parseFloat(cp.latitude), parseFloat(cp.longitude)]}>
                  <Popup>
                    <strong>CP{cp.sequence_order}: {cp.name}</strong><br />
                    {cp.scanned_at
                      ? `✅ Scanned: ${fmtTime(cp.scanned_at)} IST`
                      : (isActive ? '⏳ Pending / Awaiting Guard' : '❌ Missed / Left')}
                  </Popup>
                </Marker>
              </React.Fragment>
            ))}
          </MapContainer>
        </div>

        {/* Checkpoint timeline */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{isActive ? '📋 Checkpoint Progress (Scanned vs Pending)' : '📋 Checkpoint Timeline (Scanned vs Left)'}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span className="badge badge-success">
                ✅ {checkpoints.filter(c => c.scanned_at).length} Scanned
              </span>
              <span className={`badge ${checkpoints.filter(c => !c.scanned_at).length > 0 ? 'badge-danger' : 'badge-info'}`}>
                {checkpoints.filter(c => !c.scanned_at).length > 0
                  ? (isActive ? `⏳ ${checkpoints.filter(c => !c.scanned_at).length} Pending` : `❌ ${checkpoints.filter(c => !c.scanned_at).length} Left / Missed`)
                  : '0 Missed'}
              </span>
            </div>
          </div>
          {checkpoints.map(cp => {
            const isScanned = !!cp.scanned_at;
            return (
              <div key={cp.id} style={{
                display: 'flex', gap: 12, alignItems: 'flex-start',
                padding: '12px 14px', borderBottom: '1px solid #f1f5f9',
                background: isScanned ? '#f0fdf4' : '#fff1f2',
                borderRadius: 8, marginBottom: 6,
                border: `1px solid ${isScanned ? '#bbf7d0' : '#fecdd3'}`,
              }}>
                <div style={{ fontSize: 20, flexShrink: 0, marginTop: 2 }}>
                  {isScanned ? '✅' : '❌'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: isScanned ? '#166534' : '#9f1239' }}>
                      CP{cp.sequence_order}: {cp.name}
                    </div>
                    <span className={`badge ${isScanned ? 'badge-success' : 'badge-danger'}`}>
                      {isScanned ? 'Scanned & Verified' : (isActive ? '⏳ Pending Scan' : 'Missed / Left')}
                    </span>
                  </div>
                  {isScanned ? (
                    <div style={{ fontSize: 12, color: '#166534', marginTop: 4 }}>
                      ✓ Scanned at <strong>{fmtTimeSec(cp.scanned_at)} IST</strong>
                      {cp.distance_from_checkpoint != null && ` · ${Math.round(cp.distance_from_checkpoint)}m away`}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4, fontWeight: 500 }}>
                      {isActive ? '⏳ Checkpoint awaiting visit / scan by guard' : '⚠️ Checkpoint was not scanned or visited during this patrol'}
                    </div>
                  )}
                </div>
                {(cp.image_url || cp.image_base64) && (
                  <img
                    src={imgUrl(cp)}
                    alt="scan evidence"
                    onClick={() => setPreviewModalCp(cp)}
                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, flexShrink: 0, cursor: 'pointer', border: '1.5px solid #22c55e' }}
                    title="Click to enlarge"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Photo Evidence */}
      {checkpoints.some(cp => cp.image_url || cp.image_base64) && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 6 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>📸 Photo Evidence Log</div>
            <span style={{ fontSize: 12, color: '#64748b' }}>Click any photo to view full size</span>
          </div>
          <div className="photo-grid">
            {checkpoints.filter(cp => cp.image_url || cp.image_base64).map(cp => (
              <div
                key={cp.id}
                className="photo-card"
                onClick={() => setPreviewModalCp(cp)}
                style={{ cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
                title="Click to view full photo evidence"
              >
                <div style={{ position: 'relative', width: '100%', height: 160, background: '#f8fafc', overflow: 'hidden' }}>
                  <img
                    src={imgUrl(cp)}
                    alt={cp.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div style={{ display: 'none', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#64748b', fontSize: 12, background: '#f1f5f9' }}>
                    <span style={{ fontSize: 32, marginBottom: 4 }}>📷</span>
                    Photo Evidence Logged
                  </div>
                  <div style={{
                    position: 'absolute', bottom: 6, right: 6,
                    background: 'rgba(15,23,42,0.75)', color: 'white',
                    fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600
                  }}>
                    🔍 Enlarge
                  </div>
                </div>
                <div className="photo-card-info" style={{ padding: '10px 12px' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>CP{cp.sequence_order}: {cp.name}</div>
                  <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{cp.scanned_at && `${fmtTime(cp.scanned_at)} IST`}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 700,
                      background: cp.status === 'verified' ? '#dcfce7' : '#fee2e2',
                      color:      cp.status === 'verified' ? '#166534' : '#991b1b',
                    }}>{cp.status === 'verified' ? 'Verified' : cp.status}</span>
                    <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>View ↗</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Incidents */}
      {incidents.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>⚠️ Incidents ({incidents.length})</div>
          {incidents.map(inc => (
            <div key={inc.id} style={{
              padding: '12px 0', borderBottom: '1px solid #f1f5f9',
              display: 'flex', gap: 14, alignItems: 'flex-start',
            }}>
              {inc.image_url && (
                <img src={imgUrl(inc.image_url)} alt="incident"
                  style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, flexShrink: 0 }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{inc.title}</div>
                {inc.description && (
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{inc.description}</div>
                )}
                <span style={{
                  marginTop: 6, display: 'inline-block',
                  fontSize: 10, padding: '2px 9px', borderRadius: 20, fontWeight: 700,
                  background: (SEV[inc.severity] || '#94a3b8') + '20',
                  color: SEV[inc.severity] || '#94a3b8',
                  textTransform: 'capitalize',
                }}>{inc.severity}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Photo Enlarge Modal / Lightbox */}
      {previewModalCp && (
        <div
          onClick={() => setPreviewModalCp(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 16
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: 14,
              maxWidth: 640,
              width: '100%',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '92vh'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '14px 20px',
              background: '#0f172a',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#38bdf8' }}>
                  📸 Photo Evidence — CP #{previewModalCp.sequence_order}: {previewModalCp.name}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  Route: {patrol.route_name} • Guard: {patrol.guard_name}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPreviewModalCp(null)}
                style={{
                  background: 'none', border: 'none', color: '#94a3b8',
                  fontSize: 22, cursor: 'pointer', padding: '0 4px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Photo Container */}
            <div style={{
              background: '#090d16',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 280,
              maxHeight: '62vh',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <img
                src={imgUrl(previewModalCp)}
                alt="Full photo evidence"
                style={{
                  maxWidth: '100%',
                  maxHeight: '62vh',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div style={{ display: 'none', width: '100%', height: 280, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#94a3b8', textAlign: 'center', padding: 20 }}>
                <span style={{ fontSize: 44, marginBottom: 8 }}>📷</span>
                <div style={{ fontWeight: 700, color: '#f8fafc', fontSize: 15 }}>Photo Evidence Logged & Verified</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  Verification confirmed at {previewModalCp.scanned_at ? `${fmtTimeSec(previewModalCp.scanned_at)} IST` : '--'}.
                </div>
              </div>
            </div>

            {/* Metadata Footer */}
            <div style={{
              padding: '12px 20px',
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8
            }}>
              <div style={{ fontSize: 12, color: '#334155' }}>
                <div>
                  Scanned At: <strong>{previewModalCp.scanned_at ? `${fmtDateTime(previewModalCp.scanned_at)} IST` : '--'}</strong>
                </div>
                {previewModalCp.distance_from_checkpoint != null && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    GPS Variance: {Math.round(previewModalCp.distance_from_checkpoint)}m from checkpoint
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="badge badge-success" style={{ fontSize: 11, padding: '3px 8px' }}>
                  ✅ Verified
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPreviewModalCp(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin: stop active patrol */}
      {isAdmin && isActive && (
        <div className="card">
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>⚙️ Admin Actions</div>
          <button
            className="btn btn-danger"
            onClick={async () => {
              const reason = window.prompt('Reason for stopping:');
              if (!reason) return;
              await axios.post(`/api/patrols/${id}/stop`, { reason });
              toast.success('Patrol stopped');
              load();
            }}>
            🛑 Force Stop Patrol
          </button>
        </div>
      )}
    </div>
  );
}
